import React from 'react';

/**
 * Shared inline icon set — every glyph is currentColor so chips and buttons
 * tint them via CSS. Replaces the emoji chrome (⚡📊🔥⏱️🎉…) which rendered
 * differently on every platform.
 */

interface IconProps {
  size?: number;
  className?: string;
}

const svgProps = (size: number, className?: string) => ({
  width: size,
  height: size,
  className,
  'aria-hidden': true as const,
  focusable: false as const,
});

/** Current chat rate (last 10s) */
export const IconBolt: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="currentColor">
    <path d="M9.4 1 3.2 9.1h3.6L6.3 15l6.5-8.4H9.1L9.4 1z" />
  </svg>
);

/** 5-minute average rate */
export const IconPulse: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 8.5h2.6L6 3.5l3.4 9 2-4h3.6" />
  </svg>
);

/** Heat ratio */
export const IconFlame: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 15.2c-2.9 0-5.2-2.3-5.2-5.2 0-2.1 1.3-3.4 2.5-4.6C6.4 4.3 7.4 3.2 7.2.9c2.6 1.3 4.2 3.3 4.3 5.3.6-.3 1-.9 1.2-1.7.9 1.2 1.5 2.7 1.5 4.5 0 2.9-2.3 5.2-5.2 5.2H8zm.1-2c1.4 0 2.5-1.1 2.5-2.5 0-.8-.4-1.4-.9-2-.4.5-.9.8-1.6.9 0-1-.3-1.9-1-2.6-.2 1-.7 1.7-1.3 2.3-.5.5-.8.9-.8 1.5A2.6 2.6 0 0 0 8.1 13.2z" />
  </svg>
);

/** Stream uptime */
export const IconTimer: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
    <path d="M6 1.4h4" />
    <circle cx="8" cy="9.2" r="5.4" />
    <path d="M8 9.2V5.9" />
  </svg>
);

/** Watch party */
export const IconParty: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="currentColor">
    <path d="M1.6 14.4 5 5.8l5.2 5.2-8.6 3.4zm4.5-10.6L5 2.7c.9-.5 1.9-.4 2.7.2.4-.9.4-1.6.1-2.4l1.4-.4c.5 1.2.4 2.4-.3 3.7-1.1-.9-2-1-2.8 0zm5.6 4L10.6 6.7c1-.9 2.1-1.2 3.4-.9.2-.7 0-1.3-.5-2l1.2-.8c.9 1.1 1.1 2.4.6 3.8-1.4-.5-2.5-.4-3.6 1zM11.4 1.5a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM13.9 13.1a1.1 1.1 0 1 1 0-2.2 1.1 1.1 0 0 1 0 2.2z" />
  </svg>
);

/** Twitch glitch logo */
export const IconGlitch: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
  </svg>
);

/** Category lock (game filter) */
export const IconCrosshair: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <circle cx="8" cy="8" r="5.2" />
    <path d="M8 1v3M8 12v3M1 8h3M12 8h3" />
  </svg>
);

/** Share a list */
export const IconShare: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12.2" cy="3.2" r="2" />
    <circle cx="3.8" cy="8" r="2" />
    <circle cx="12.2" cy="12.8" r="2" />
    <path d="M5.6 7.1 10.4 4M5.6 8.9l4.8 3.1" />
  </svg>
);

export const IconSearch: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="7" cy="7" r="4.6" />
    <path d="m10.6 10.6 3.6 3.6" />
  </svg>
);

/** Settings (sliders) */
export const IconSliders: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
    <path d="M2 4h12M2 8h12M2 12h12" />
    <circle cx="10.5" cy="4" r="1.9" fill="var(--surface, #18181b)" />
    <circle cx="5" cy="8" r="1.9" fill="var(--surface, #18181b)" />
    <circle cx="11.5" cy="12" r="1.9" fill="var(--surface, #18181b)" />
  </svg>
);

/** Blacklist */
export const IconBan: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
    <circle cx="8" cy="8" r="6.2" />
    <path d="M3.7 3.7l8.6 8.6" />
  </svg>
);

/** Step through ranked list / collapse sidebar */
export const IconChevronLeft: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 3 5 8l5 5" />
  </svg>
);

/** Reveal sidebar */
export const IconPanel: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <rect x="1.8" y="2.5" width="12.4" height="11" rx="1.5" />
    <path d="M6.2 2.5v11" />
  </svg>
);

export const IconLogout: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="currentColor">
    <path d="M6 1h5a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v1H4V3a2 2 0 0 1 2-2z" />
    <path d="M1 8a.5.5 0 0 1 .5-.5h5.793L5.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L7.293 8.5H1.5A.5.5 0 0 1 1 8z" />
  </svg>
);

/** Open on Twitch (external) */
export const IconExternal: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6.5 3H4a1.5 1.5 0 0 0-1.5 1.5V12A1.5 1.5 0 0 0 4 13.5h7.5A1.5 1.5 0 0 0 13 12V9.5" />
    <path d="M9.5 2h4.5v4.5" />
    <path d="M14 2 7.8 8.2" />
  </svg>
);

export const IconDiscord: React.FC<IconProps> = ({ size = 16, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

export const IconX: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const IconChevronDown: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m4 6 4 4 4-4" />
  </svg>
);

export const IconRadio: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.93 11.07a4.5 4.5 0 0 1 0-6.14M11.07 4.93a4.5 4.5 0 0 1 0 6.14M2.8 13.2a7.5 7.5 0 0 1 0-10.4M13.2 2.8a7.5 7.5 0 0 1 0 10.4" />
    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
  </svg>
);

export const IconCheck: React.FC<IconProps> = ({ size = 14, className }) => (
  <svg {...svgProps(size, className)} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3.5 8.5 6.5 11.5 12.5 4.5" />
  </svg>
);
